import type { MetricDashboardSource } from "@/metrics/catalog"

import type { TargetCondition } from "@/types/metrics/contracts"
import { calculatePresetDateRange } from "@/utils/date/dateRange"
import {
  BUCKET_INTERVAL,
  type BucketInterval,
} from "@/utils/date/timeBucketing"

import type { ChartDataPoint, ChartWidgetConfig } from "../../types/chart"
import { GROUP_BY_TYPE } from "../../types/chart-types"
import {
  AGGREGATION_TYPE,
  TIME_MODE,
  VISUALIZATION_TYPE,
  type BaselineDirection,
  type GlobalDateRange,
} from "../../types/core"
import { DEFAULT_WIDGET_DATE_RANGE } from "../../types/dateRange"
import type { DisplayNameLookups } from "./displayNameUtils"

export interface DashboardChartRow {
  run_occurred_at?: string
  string_value?: string
  value?: number | string | null
  avg_value?: number | string | null
  sum_value?: number | string | null
  min_value?: number | string | null
  max_value?: number | string | null
  count_value?: number | string | null
  run_agent_id?: string | null
  mutation_id?: string | null
  persona_id?: string | null
  run_template_name?: string | null
  test_set_id?: string | null
  assignee?: string | null
  metadata_value?: string | null
  metadata_group_kind?: "value" | "remainder" | null
  metadata_top_values?: string[] | null
}

export interface BuildChartDataParams {
  bucketInterval: BucketInterval
  config: ChartWidgetConfig
  data?: DashboardChartRow[] | { value?: number }[] | null
  effectiveDateRange?: GlobalDateRange
  isHistogramVisualization: boolean
  isTopListVisualization: boolean
  isWholeRangeVisualization?: boolean
}

interface BuildDashboardMetricQueryInputParams {
  bucketInterval: BucketInterval
  config: ChartWidgetConfig
  effectiveDateRange?: GlobalDateRange
  effectiveTargetCondition?: TargetCondition
  isHistogramVisualization: boolean
  isMonitoring: boolean
  isTopListVisualization: boolean
  isWholeRangeVisualization?: boolean
  metricId?: string | null
  timezone: string
}

interface BuildDashboardStatisticQueryInputParams {
  config: ChartWidgetConfig
  effectiveDateRange?: GlobalDateRange
  effectiveTargetCondition?: TargetCondition
  isMonitoring: boolean
  metricId?: string | null
}

interface BuildHumanReviewMetricQueryInputParams {
  bucketInterval: BucketInterval
  config: ChartWidgetConfig
  effectiveDateRange?: GlobalDateRange
  timezone: string
}

interface CreateChartBaselineDataParams {
  baselineDirection?: BaselineDirection
  baselineHistory?: Array<{
    baseline_mean: number
    baseline_sigma: number
    bucket_time: string
  }>
  baselineRecord?: {
    baseline_float: number | null
    baseline_sigma: number | null
    direction: BaselineDirection
  }
}

interface CreateChartExportDataKeyParams {
  chartData: ChartDataPoint[]
  lookups: DisplayNameLookups | null
  splitKeys: string[]
  stableDateRange: {
    end: Date
    start: Date
  }
}

interface GetEffectiveChartDateRangeParams {
  globalDateRange: GlobalDateRange | null
  globalDateRangeOverride: GlobalDateRange | null
  isOverrideActive: boolean
}

const getChartQueryGroupBy = (
  groupBy?: ChartWidgetConfig["groupBy"],
  splitBy?: ChartWidgetConfig["splitBy"],
): "agent" | "mutation" | "persona" | "template" | "test_set" | undefined => {
  const dimension = groupBy || splitBy
  if (dimension === GROUP_BY_TYPE.ASSIGNEE) {
    return undefined
  }
  return dimension
}

const getHumanReviewChartQueryGroupBy = (
  groupBy?: ChartWidgetConfig["groupBy"],
  splitBy?: ChartWidgetConfig["splitBy"],
): "assignee" | undefined => {
  const dimension = groupBy || splitBy
  return dimension === GROUP_BY_TYPE.ASSIGNEE
    ? GROUP_BY_TYPE.ASSIGNEE
    : undefined
}

const toFiniteNumber = (value: number | string | null | undefined) => {
  if (value == null || (typeof value === "string" && value.trim() === "")) {
    return undefined
  }

  const numberValue = Number(value)
  return Number.isFinite(numberValue) ? numberValue : undefined
}

export const getRowValue = (
  item: DashboardChartRow,
  aggregation: ChartWidgetConfig["aggregation"],
): number | undefined => {
  switch (aggregation) {
    case AGGREGATION_TYPE.COUNT:
      return toFiniteNumber(item.count_value ?? item.value)
    case AGGREGATION_TYPE.SUM:
      return toFiniteNumber(item.sum_value ?? item.value)
    case AGGREGATION_TYPE.MIN:
      return toFiniteNumber(item.min_value ?? item.value)
    case AGGREGATION_TYPE.MAX:
      return toFiniteNumber(item.max_value ?? item.value)
    case AGGREGATION_TYPE.AVG:
      return toFiniteNumber(item.avg_value ?? item.value)
    case AGGREGATION_TYPE.SUCCESS:
    case AGGREGATION_TYPE.P50:
    case AGGREGATION_TYPE.P90:
    case AGGREGATION_TYPE.P95:
    case AGGREGATION_TYPE.P99:
    default:
      return toFiniteNumber(item.value)
  }
}

export const getChartBucketInterval = (
  bucketInterval?: string,
): BucketInterval =>
  (bucketInterval as BucketInterval) || BUCKET_INTERVAL.FOUR_HOURS

export const getIsWholeRangeBarVisualization = (
  config: Pick<ChartWidgetConfig, "timeMode" | "visualizationType">,
): boolean =>
  config.visualizationType === VISUALIZATION_TYPE.BAR &&
  (config.timeMode ?? TIME_MODE.OVER_TIME) === TIME_MODE.WHOLE_RANGE

export const getChartBucketIntervalMs = (bucketInterval?: string): number =>
  (
    ({
      [BUCKET_INTERVAL.FIFTEEN_MINUTES]: 15 * 60 * 1000,
      [BUCKET_INTERVAL.ONE_HOUR]: 60 * 60 * 1000,
      [BUCKET_INTERVAL.FOUR_HOURS]: 4 * 60 * 60 * 1000,
      [BUCKET_INTERVAL.ONE_DAY]: 24 * 60 * 60 * 1000,
    }) as const
  )[getChartBucketInterval(bucketInterval)] ?? 4 * 60 * 60 * 1000

export const getChartZoomOverride = (
  range: { start: Date; end: Date },
  bucketInterval?: string,
  now = new Date(),
): GlobalDateRange => ({
  start: range.start,
  end: new Date(
    Math.min(
      range.end.getTime() + getChartBucketIntervalMs(bucketInterval) - 1,
      now.getTime(),
    ),
  ),
})

export const getEffectiveChartDateRange = ({
  globalDateRange,
  globalDateRangeOverride,
  isOverrideActive,
}: GetEffectiveChartDateRangeParams): GlobalDateRange => {
  if (isOverrideActive && globalDateRangeOverride) {
    return globalDateRangeOverride
  }

  if (globalDateRange) {
    return globalDateRange
  }

  return calculatePresetDateRange(DEFAULT_WIDGET_DATE_RANGE.preset!)
}

export const resolveChartMetric = (
  allMetricRecords: Record<string, MetricDashboardSource> | undefined,
  metricId?: string,
): MetricDashboardSource | null => {
  if (!allMetricRecords || !metricId) {
    return null
  }

  return (
    Object.values(allMetricRecords).find((metric) => metric.id === metricId) ??
    null
  )
}

export const getChartTargetValues = (
  metric?: MetricDashboardSource | null,
): string[] | undefined => {
  const targetCondition = metric?.metric_metadata?.target_condition
  return targetCondition && "target_values" in targetCondition
    ? targetCondition.target_values
    : undefined
}

export const createChartBaselineData = ({
  baselineDirection,
  baselineHistory,
  baselineRecord,
}: CreateChartBaselineDataParams) => {
  if (
    baselineRecord?.baseline_float == null ||
    baselineRecord.baseline_sigma == null
  ) {
    return null
  }

  return {
    baseline_float: baselineRecord.baseline_float,
    baseline_sigma: baselineRecord.baseline_sigma,
    direction: baselineDirection ?? baselineRecord.direction,
    points: (baselineHistory ?? []).map((historyPoint) => ({
      timestamp: new Date(historyPoint.bucket_time).getTime(),
      mean: historyPoint.baseline_mean,
      sigma: historyPoint.baseline_sigma,
    })),
  }
}

export const createChartExportDataKey = ({
  chartData,
  lookups,
  splitKeys,
  stableDateRange,
}: CreateChartExportDataKeyParams): string =>
  JSON.stringify({
    chartData,
    splitKeys,
    stableDateRange: {
      start: stableDateRange.start.getTime(),
      end: stableDateRange.end.getTime(),
    },
    lookups: lookups
      ? {
          agents: Array.from(lookups.agents.entries()),
          mutations: Array.from(lookups.mutations.entries()),
          personas: Array.from(lookups.personas.entries()),
          templates: Array.from(lookups.templates.entries()),
          testSets: Array.from(lookups.testSets.entries()),
        }
      : null,
  })

export const buildDashboardMetricQueryInput = ({
  bucketInterval,
  config,
  effectiveDateRange,
  effectiveTargetCondition,
  isHistogramVisualization,
  isMonitoring,
  isTopListVisualization,
  isWholeRangeVisualization,
  metricId,
  timezone,
}: BuildDashboardMetricQueryInputParams) => ({
  metricId: config.metricId || "",
  metricOutputType: config.metricOutputType,
  aggregation: config.aggregation,
  monitoring: isMonitoring,
  startDate: effectiveDateRange?.start.toISOString(),
  endDate: effectiveDateRange?.end.toISOString(),
  bucketInterval,
  rawValues: isHistogramVisualization,
  skipBucketing: Boolean(isTopListVisualization || isWholeRangeVisualization),
  filters: config.filters,
  groupBy: isHistogramVisualization
    ? undefined
    : getChartQueryGroupBy(config.groupBy, config.splitBy),
  groupByMetadataKey: isHistogramVisualization
    ? undefined
    : config.groupByMetadataKey,
  timezone,
  metricFilter: config.metricFilter,
  targetConditionsByMetricId:
    effectiveTargetCondition && metricId
      ? { [metricId]: effectiveTargetCondition }
      : undefined,
})

export const buildDashboardStatisticQueryInput = ({
  config,
  effectiveDateRange,
  effectiveTargetCondition,
  isMonitoring,
  metricId,
}: BuildDashboardStatisticQueryInputParams) => ({
  metricId: config.metricId || "",
  metricOutputType: config.metricOutputType,
  aggregation: config.aggregation,
  monitoring: isMonitoring,
  startDate: effectiveDateRange?.start.toISOString(),
  endDate: effectiveDateRange?.end.toISOString(),
  filters: config.filters,
  groupBy: getChartQueryGroupBy(config.groupBy, config.splitBy),
  groupByMetadataKey: config.groupByMetadataKey,
  metricFilter: config.metricFilter,
  targetConditionsByMetricId:
    config.aggregation === AGGREGATION_TYPE.SUCCESS &&
    effectiveTargetCondition &&
    metricId
      ? { [metricId]: effectiveTargetCondition }
      : undefined,
})

export const buildHumanReviewMetricQueryInput = ({
  bucketInterval,
  config,
  effectiveDateRange,
  timezone,
}: BuildHumanReviewMetricQueryInputParams) => ({
  metricId: config.metricId || "",
  metricOutputType: config.metricOutputType,
  aggregation: config.aggregation,
  startDate: effectiveDateRange?.start.toISOString(),
  endDate: effectiveDateRange?.end.toISOString(),
  bucketInterval,
  timezone,
  filters: config.filters,
  groupBy: getHumanReviewChartQueryGroupBy(config.groupBy, config.splitBy),
  metricFilter: config.metricFilter,
})

export const shouldEnableFlexibleChartQuery = ({
  aggregation,
  effectiveDateRange,
  effectiveTargetCondition,
  isHumanReview,
  metricId,
  metricIsBinary,
  metricOutputType,
}: {
  aggregation?: ChartWidgetConfig["aggregation"]
  effectiveDateRange?: GlobalDateRange
  effectiveTargetCondition?: TargetCondition
  isHumanReview: boolean
  metricId?: string
  metricIsBinary: boolean
  metricOutputType?: ChartWidgetConfig["metricOutputType"]
}) =>
  !isHumanReview &&
  Boolean(metricId) &&
  Boolean(effectiveDateRange) &&
  (aggregation !== AGGREGATION_TYPE.SUCCESS ||
    Boolean(effectiveTargetCondition) ||
    metricOutputType === "string" ||
    metricIsBinary)

export const shouldEnableStatisticChartQuery = ({
  effectiveDateRange,
  isHumanReview,
  isStatisticVisualization,
  metricId,
}: {
  effectiveDateRange?: GlobalDateRange
  isHumanReview: boolean
  isStatisticVisualization: boolean
  metricId?: string
}) =>
  !isHumanReview &&
  Boolean(metricId) &&
  Boolean(effectiveDateRange) &&
  isStatisticVisualization

export const shouldEnableHumanReviewChartQuery = ({
  effectiveDateRange,
  isHumanReview,
  metricId,
}: {
  effectiveDateRange?: GlobalDateRange
  isHumanReview: boolean
  metricId?: string
}) => isHumanReview && Boolean(metricId) && Boolean(effectiveDateRange)

export const hasChartSuccessCriteriaError = ({
  config,
  effectiveTargetCondition,
  isMetricLoading,
  metricIsBinary,
}: {
  config: ChartWidgetConfig
  effectiveTargetCondition?: TargetCondition
  isMetricLoading: boolean
  metricIsBinary: boolean
}) =>
  !isMetricLoading &&
  config.aggregation === AGGREGATION_TYPE.SUCCESS &&
  !effectiveTargetCondition &&
  config.metricOutputType !== "string" &&
  !metricIsBinary
